import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LocationProvider } from "@/contexts/LocationContext";

// HomePage stays eager so the landing page needs no extra chunk fetch (protects LCP).
import WarmHome from "@/themes/warm_home_like/pages/HomePage";

// Secondary pages are code-split: their JS (incl. the heavy contact date-picker) only
// loads on navigation, shrinking the initial bundle. No visual change — pages render
// identically once their chunk arrives; the fallback is an empty min-height spacer.
const WarmServices = lazy(() => import("@/themes/warm_home_like/pages/ServicesPage"));
const WarmAbout = lazy(() => import("@/themes/warm_home_like/pages/AboutPage"));
const WarmTestimonials = lazy(() => import("@/themes/warm_home_like/pages/TestimonialsPage"));
const WarmGallery = lazy(() => import("@/themes/warm_home_like/pages/GalleryPage"));
const WarmContact = lazy(() => import("@/themes/warm_home_like/pages/ContactPage"));
const WarmBlog = lazy(() => import("@/themes/warm_home_like/pages/BlogPage"));
const WarmBlogPost = lazy(() => import("@/themes/warm_home_like/pages/BlogPostPage"));
const WarmPrivacy = lazy(() => import("@/themes/warm_home_like/pages/PrivacyPage"));
const WarmTerms = lazy(() => import("@/themes/warm_home_like/pages/TermsPage"));

import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// Minimal, non-flashing fallback while a route chunk loads.
const RouteFallback = () => <div className="min-h-screen" aria-hidden="true" />;

// Wrapper components for each location
function LynnwoodRoutes() {
  return (
    <LocationProvider locationId="lynnwood">
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<WarmHome />} />
          <Route path="/services" element={<WarmServices />} />
          <Route path="/about" element={<WarmAbout />} />
          <Route path="/testimonials" element={<WarmTestimonials />} />
          <Route path="/gallery" element={<WarmGallery />} />
          <Route path="/contact" element={<WarmContact />} />
          <Route path="/blog" element={<WarmBlog />} />
          <Route path="/blog/:slug" element={<WarmBlogPost />} />
          <Route path="/privacy" element={<WarmPrivacy />} />
          <Route path="/terms" element={<WarmTerms />} />
        </Routes>
      </Suspense>
    </LocationProvider>
  );
}

function SeattleRoutes() {
  return (
    <LocationProvider locationId="seattle">
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<WarmHome />} />
          <Route path="/services" element={<WarmServices />} />
          <Route path="/about" element={<WarmAbout />} />
          <Route path="/testimonials" element={<WarmTestimonials />} />
          <Route path="/gallery" element={<WarmGallery />} />
          <Route path="/contact" element={<WarmContact />} />
          <Route path="/blog" element={<WarmBlog />} />
          <Route path="/blog/:slug" element={<WarmBlogPost />} />
          <Route path="/privacy" element={<WarmPrivacy />} />
          <Route path="/terms" element={<WarmTerms />} />
        </Routes>
      </Suspense>
    </LocationProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {/*
          Safety wrapper: ensures LocationContext is always available (avoids transient/hot-reload edge cases).
          Per-location providers below still override this default value.
        */}
        <LocationProvider>
          <BrowserRouter>
            <Routes>
              {/* Lynnwood (default) routes */}
              <Route path="/*" element={<LynnwoodRoutes />} />

              {/* Seattle routes */}
              <Route path="/seattle/*" element={<SeattleRoutes />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </LocationProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
